/**
 * Anneau de progression style Apple Watch.
 * Empilable via <RingStack /> pour superposer 2-3 métriques.
 *
 * NB : on utilise des couleurs hex inline (et pas des classes Tailwind dynamiques)
 * pour rester compatible avec le purge JIT.
 */

export interface RingProps {
  /** 0..1 (clampé pour l'arc, mais peut dépasser pour signaler dépassement) */
  value: number;
  /** Couleur en hex (ex '#f59e0b') */
  color: string;
  /** Couleur de la piste de fond (par défaut version assombrie) */
  trackColor?: string;
  /** Diamètre extérieur du SVG (px) */
  size?: number;
  /** Épaisseur de l'anneau (px) */
  thickness?: number;
  /** Décalage interne du rayon (utilisé pour empiler) */
  inset?: number;
  /** Label invisible pour lecteurs d'écran */
  ariaLabel?: string;
  /** Animation à l'apparition */
  animate?: boolean;
}

export const Ring = ({
  value,
  color,
  trackColor,
  size = 140,
  thickness = 12,
  inset = 0,
  ariaLabel,
  animate = true,
}: RingProps) => {
  const radius = (size - thickness) / 2 - inset;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(1, value));
  const dash = circumference * clamped;
  const track = trackColor ?? hexWithAlpha(color, 0.15);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={ariaLabel}
      style={{ display: 'block' }}
    >
      <circle
        cx={cx} cy={cy} r={radius}
        fill="none"
        stroke={track}
        strokeWidth={thickness}
      />
      <circle
        cx={cx} cy={cy} r={radius}
        fill="none"
        stroke={color}
        strokeWidth={thickness}
        strokeLinecap="round"
        style={{
          strokeDasharray: `${dash} ${circumference}`,
          transform: 'rotate(-90deg)',
          transformOrigin: 'center',
          transition: animate ? 'stroke-dasharray 700ms cubic-bezier(0.16, 1, 0.3, 1)' : undefined,
        }}
      />
    </svg>
  );
};

const hexWithAlpha = (hex: string, alpha: number): string => {
  const m = /^#?([a-f\d]{6})$/i.exec(hex);
  if (!m) return hex;
  const a = Math.round(alpha * 255).toString(16).padStart(2, '0');
  return `#${m[1]}${a}`;
};

export interface RingDef {
  value: number;
  color: string;
  ariaLabel?: string;
}

export interface RingStackProps {
  rings: RingDef[];
  size?: number;
  thickness?: number;
  /** Espace entre anneaux empilés */
  gap?: number;
  /** Contenu central (compteur, icône, etc.) */
  children?: React.ReactNode;
  className?: string;
}

export const RingStack = ({ rings, size = 160, thickness = 12, gap = 4, children, className = '' }: RingStackProps) => (
  <div className={['relative inline-block', className].filter(Boolean).join(' ')} style={{ width: size, height: size }}>
    {rings.map((r, i) => (
      <div key={i} className="absolute inset-0">
        <Ring
          value={r.value}
          color={r.color}
          size={size}
          thickness={thickness}
          inset={i * (thickness + gap)}
          ariaLabel={r.ariaLabel}
        />
      </div>
    ))}
    {children && (
      <div className="absolute inset-0 flex items-center justify-center text-center">
        {children}
      </div>
    )}
  </div>
);

/** Palette officielle des anneaux APOLLO */
export const RING_COLORS = {
  calories: '#f59e0b',     // ambre — accent principal
  protein:  '#10b981',     // émeraude
  water:    '#3b82f6',     // bleu
  sets:     '#a855f7',     // violet (4e anneau optionnel)
} as const;
